import React, { useEffect, useState, useRef } from 'react';
import { useAuth } from '../../context/AuthContext';
import { AdService } from '../../services/AdService';
import { Ad } from '../../types/ads';
import { useSubscription } from '../../hooks/useSubscription';

export const AdBanner: React.FC = () => {
    const { userData } = useAuth();
    const [currentAd, setCurrentAd] = useState<Ad | null>(null);
    const [isVisible, setIsVisible] = useState(false);
    const [ads, setAds] = useState<Ad[]>([]);
    const intervalRef = useRef<NodeJS.Timeout | null>(null);

    // Weighted Random Selection
    const selectAd = (availableAds: Ad[]): Ad | null => {
        if (availableAds.length === 0) return null;
        if (availableAds.length === 1) return availableAds[0];

        // Calculate total weight
        const totalWeight = availableAds.reduce((sum, ad) => sum + (ad.priority || 1), 0);
        let random = Math.random() * totalWeight;

        for (const ad of availableAds) {
            const weight = ad.priority || 1;
            if (random < weight) {
                return ad;
            }
            random -= weight;
        }

        return availableAds[0]; // Fallback
    };

    const { limits } = useSubscription();

    useEffect(() => {
        const fetchAds = async () => {
            if (!userData) return;

            const shouldShow = limits.hasAds; // Dynamic from Firestore
            setIsVisible(shouldShow);

            if (shouldShow) {
                // Pass true/limits to avoid double checking or just rely on the component check
                const fetchedAds = await AdService.getActiveAds(userData, limits);
                setAds(fetchedAds);

                if (fetchedAds.length > 0) {
                    const selected = selectAd(fetchedAds);
                    if (selected) {
                        setCurrentAd(selected);
                        AdService.trackImpression(selected.id);
                    }
                }
            }
        };

        fetchAds();
    }, [userData]);

    // Rotation Logic (30s)
    useEffect(() => {
        if (ads.length > 1 && isVisible) {
            intervalRef.current = setInterval(() => {
                const nextAd = selectAd(ads);
                if (nextAd && nextAd.id !== currentAd?.id) {
                    setCurrentAd(nextAd);
                    AdService.trackImpression(nextAd.id);
                }
            }, 30000); // 30 seconds
        }

        return () => {
            if (intervalRef.current) clearInterval(intervalRef.current);
        };
    }, [ads, isVisible, currentAd]);

    if (!isVisible || !currentAd) return null;

    const handleClick = () => {
        AdService.trackClick(currentAd.id);
        window.open(currentAd.linkUrl, '_blank');
    };

    return (
        <div className="w-full bg-surface-2 border-b border-glass-border flex justify-center items-center relative overflow-hidden group cursor-pointer animate-fadeIn" onClick={handleClick}>
            <div className="absolute top-0 right-0 bg-black/40 text-[10px] px-1 text-white/70 rounded-bl backdrop-blur-sm z-10">
                Sponsor
            </div>
            <div className="w-full h-full relative">
                {currentAd.imageUrl ? (
                    <img
                        key={currentAd.id} // Force re-render for animation
                        src={currentAd.imageUrl}
                        alt={currentAd.title}
                        className="w-full h-auto max-h-32 object-cover animate-fadeIn"
                    />
                ) : (
                    <div className="h-24 flex items-center justify-center text-text-secondary text-sm font-medium bg-surface-1">
                        {currentAd.title}
                    </div>
                )}
            </div>
        </div>
    );
};
